import {array_each, is_promise, kebab_case} from 'lib/utilities';

export function for_node<T extends Node>(nodes: string | T | ArrayLike<T> | null | undefined, executor: (node: T, index: number) => any) {
    if (typeof nodes === 'string') {
        for_node<Node>(document.querySelectorAll(nodes), executor);
    } else if (nodes instanceof Node) {
        executor(nodes, 0);
    } else if (nodes) {
        array_each(nodes, executor);
    }
}

export function for_element<T extends Element>(nodes: string | T | ArrayLike<T> | null | undefined, executor: (node: T, index: number) => any) {
    for_node(nodes, executor);
}

export function query_children<T extends Element = Element>(element: T, selector: string): T[] {
    const matches: T[] = [];

    if (element) {
        for (const child of element.children) {
            if (element_matches(child, selector)) {
                matches.push(child as T);
            }
        }
    }

    return matches;
}

export function direct_child<T extends Element = Element>(element: T, selector: string): T | null {
    if (element) {
        for (const child of element.children) {
            if (element_matches(child, selector)) {
                return child as T;
            }
        }
    }

    return null;
}

export function element_matches(element: Element, selector: string): boolean {
    if (typeof element.matches === 'function') {
        return element.matches(selector);
    } else if (typeof (element as any).msMatchesSelector === 'function') {
        return (element as any).msMatchesSelector(selector);
    }
}

export function element_closest(element: Element, selector: string) {
    while (element && element.nodeType === 1) {
        if (element_matches(element, selector)) {
            return element;
        }

        element = element.parentElement;
    }

    return null;
}

/**
 * Generate the parser function with the guinea pig element inside the closure.
 */
function generate_parse_xml_function() {
    const PARSER = document.createElement('div');

    return function (xml) {
        // Use browser's XML parser
        PARSER.innerHTML = xml;

        const elements = Array.from(PARSER.childNodes);

        // Remove the children from the PARSER, don't just do PARSER.innerHTML = '' as that causes the nodes in the
        // reference to a parsed SVG to be destroyed and removed from the returned array of child nodes.
        array_each(elements, element => PARSER.removeChild(element));

        return elements;
    }
}

export const parse_xml = generate_parse_xml_function();

export function on_window_load(listener?: () => any): Promise<void> {
    return new Promise(resolve => {
        function done() {
            listener && listener();
            resolve();
        }

        if (document.readyState === 'complete') {
            setTimeout(done, 0);
        } else {
            window.addEventListener('load', done);
        }
    });
}

export function device_supports_touch() {
    if (window.PointerEvent && 'maxTouchPoints' in navigator) {
        // Nested if so we don't jump into the else block if the above condition is met and maxTouchPoints is zero
        if (navigator.maxTouchPoints > 0) {
            return true;
        }
    } else {
        if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
            // pointer: coarse - usually is a touchscreen
            return true;
        } else if (window.TouchEvent || 'ontouchstart' in window) {
            // last resort - check for exposed touch events API / event handler
            return true;
        }
    }

    return false;
}

export function get_ie_version(): number {
    const ua = window.navigator.userAgent;

    const msie = ua.indexOf('MSIE ');

    if (msie > 0) {
        // IE 10 or older => return version number
        return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');

    if (trident > 0) {
        // IE 11 => return version number
        const rv = ua.indexOf('rv:');

        return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    const edge = ua.indexOf('Edge/');

    if (edge > 0) {
        // Edge => return version number
        return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }

    // other browser
    return 0;
}

/**
 * Copy attributes from one element to another.
 *
 * @param source
 * @param dest
 * @param exclude
 */
export function copy_attributes(source: Element, dest: Element, exclude: string[] = []) {
    for (const attr of Array.from(source.attributes)) {
        if (exclude.indexOf(attr.name) === -1) {
            dest.setAttribute(attr.name, attr.value);
        }
    }
}

/**
 * Set the class if the condition is truthy, removes it otherwise.
 *
 * Returns the truthiness of the condition.
 */
export function set_class(element: Element, name: string, condition: any): boolean {
    if (condition) {
        element?.classList.add(name);
    } else {
        element?.classList.remove(name);
    }

    return !! condition;
}

/**
 * Set multiple classes on an element conditionally.
 *
 * @param element
 * @param classConditionMap - An object where the key denotes the class, and the value is the condition
 */
export function set_classes(element: Element, classConditionMap: {[name: string]: any}) {
    for (const className in classConditionMap) {
        if (classConditionMap.hasOwnProperty(className)) {
            set_class(element, className, classConditionMap[className]);
        }
    }
}

export function add_class(nodes: ArrayLike<Element> | Element | undefined | null, ...names: string[]) {
    for_element(nodes, n => n.classList.add(...names));
}

export function remove_class(nodes: ArrayLike<Element> | Element | undefined | null, ...names: string[]) {
    for_element(nodes, n => n.classList.remove(...names));
}

export function toggle_class(nodes: ArrayLike<Element> | Element | undefined | null, name: string) {
    for_element(nodes, n => n.classList.toggle(name));
}

export function parse_transition_duration(duration: string): number {
    const durations = duration
        .split(',')
        .map(d => parseFloat(d));

    // Convert into milliseconds
    return Math.max(0, ...durations) * 1000;
}

/**
 * Get the transition duration on an element.
 *
 * @param element
 */
export function get_transition_duration(element: Element) {
    return parse_transition_duration(
        getComputedStyle(element).transitionDuration
    );
}

/**
 * Get the height of an element with the given styles temporarily applied. Any previous
 * styles overwritten will be restored after the height has been retrieved.
 *
 * @param element
 * @param styles
 */
export function get_height_with_styles(element: HTMLElement, styles: StyleObject): number {
    return temporarily_override_styles(element, styles, () => element.offsetHeight);
}

type StylePropertyPriority = 'important' | '' | undefined;

export interface StyleObject {
    [name: string]: string | {
        value: string,
        priority?: StylePropertyPriority
    }
}

/**
 * Get the specified style properties from an element.
 *
 * Returns a value compatible with the input for the set_styles function.
 *
 * @param element
 * @param props
 */
export function get_styles(element: HTMLElement, props: string[]): StyleObject {
    const map: StyleObject = {};

    props.forEach(originalProp => {
        const prop = kebab_case(originalProp);

        map[originalProp] = {
            value: element.style.getPropertyValue(prop),
            priority: element.style.getPropertyPriority(prop) as StylePropertyPriority
        }
    });

    return map;
}

/**
 * Set style properties on an element.
 *
 * @param element
 * @param styles
 * @param priorityOverride
 */
export function set_styles(element: HTMLElement, styles: StyleObject, priorityOverride?: StylePropertyPriority) {
    for (const originalProp in styles) {
        if (styles.hasOwnProperty(originalProp)) {
            const value = styles[originalProp];
            const prop = kebab_case(originalProp);

            switch (typeof value) {
                case 'string':
                case 'number':
                    element.style.setProperty(prop, `${value}`, priorityOverride);
                    break;

                case 'object':
                    element.style.setProperty(prop, value.value, priorityOverride ?? value.priority);
                    break;
            }
        }
    }
}

/**
 * Remove the given style properties from the element.
 *
 * @param element
 * @param props
 */
export function remove_styles(element: HTMLElement, props: string[]) {
    for (const prop of props) {
        element.style.removeProperty(prop);
    }
}

/**
 * Temporarily override styles, and execute a function, returning the return value of that function.
 *
 * Any overwritten styles will be restored.
 *
 * @param element
 * @param styles
 * @param executor
 */
export function temporarily_override_styles<R, E extends HTMLElement>(element: E, styles: StyleObject, executor: (el: E) => R): R {
    const backup = get_styles(element, Object.keys(styles));

    set_styles(element, styles, 'important');

    // Force re-calculation of updated style properties
    element.getBoundingClientRect();

    const ret = executor(element);

    const reset = () => {
        // Force re-calculation of updated style properties before restoring the overridden styles
        element.getBoundingClientRect();

        set_styles(element, {
            ...backup
        });
    }

    if (is_promise(ret)) {
        ret.then(reset);
    } else {
        reset();
    }

    return ret;
}

/**
 * Get the natural height of an element, temporarily removing any fixed height to obtain it.
 *
 * @param {HTMLElement} element
 * @return {number}
 */
export function get_natural_height(element: HTMLElement): number {
    return get_height_with_styles(element, {
        height: 'auto'
    });
}

export function is_multiline_text(element: HTMLElement) {
    const style = getComputedStyle(element);
    const paddingY = parseInt(style.paddingTop) + parseInt(style.paddingBottom) + parseInt(style.borderTopWidth) + parseInt(style.borderBottomWidth);
    const lineHeight = parseInt(style.lineHeight);
    const actualHeight = element.getBoundingClientRect().height;
    const actualLineHeight = actualHeight - paddingY;

    return actualLineHeight > lineHeight * 1.5
}

export function node_each<T extends Node>(nodes: string | T | ArrayLike<T> | null | undefined, executor: (node: T, index: number) => any) {
    if (typeof nodes === 'string') {
        node_each<Node>(document.querySelectorAll(nodes), executor);
    } else if (nodes instanceof Node) {
        executor(nodes, 0);
    } else if (nodes) {
        array_each(nodes, executor);
    }
}

/**
 * Get a definition object of the box of the element, containing parsed numeric
 * pixel values for margins, paddings and including the ClientRect of the element.
 *
 * @param element
 */
export function get_element_box(element: Element) {
    const style = getComputedStyle(element);

    return {
        style,
        rect: element.getBoundingClientRect(),
        boxSizing: style.boxSizing,
        marginTop: parseInt(style.marginTop),
        marginRight: parseInt(style.marginRight),
        marginBottom: parseInt(style.marginBottom),
        marginLeft: parseInt(style.marginLeft),
        paddingTop: parseInt(style.paddingTop),
        paddingRight: parseInt(style.paddingRight),
        paddingBottom: parseInt(style.paddingBottom),
        paddingLeft: parseInt(style.paddingLeft),
        marginY: parseInt(style.marginTop) + parseInt(style.marginBottom),
        marginX: parseInt(style.marginLeft) + parseInt(style.marginRight),
        paddingY: parseInt(style.paddingTop) + parseInt(style.paddingBottom),
        paddingX: parseInt(style.paddingLeft) + parseInt(style.paddingRight)
    }
}

export function is_svg(node: any): node is SVGElement {
    return (node instanceof Element) && node.tagName.toUpperCase() === 'SVG';
}

// function isStyleRule(rule: CSSRule): rule is CSSStyleRule {
//     return rule.type === CSSRule.STYLE_RULE;
// }
//
// export function query_style(prop: string | keyof CSSStyleDeclaration, value: string) {
//     for (let i = 0; i < document.styleSheets.length; i++) {
//         const style: CSSStyleSheet = document.styleSheets[i];
//
//         for (let j = 0; j < style.cssRules.length; j++) {
//             const rule = style.cssRules[j];
//
//             if (rule.cssText.indexOf(prop as string) === -1 || rule.cssText.indexOf(value) === -1 || rule.type !== CSSRule.STYLE_RULE) {
//                 continue;
//             }
//
//             if (rule.type === CSSRule.STYLE_RULE) {
//
//             }
//         }
//     }
// }